import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { connectivity } from '../lib/connectivity';
import {
  statusBorderClass,
  summarizeJobSync,
  type JobSyncSummary,
} from '../lib/jobStatus';
import { listAppraisals, type AppraisalSummary } from '../lib/session';
import { drain, retryFailedItems } from '../lib/syncQueue';

type Tab = 'Scheduled' | 'Recent' | 'All';
const TABS: Tab[] = ['Scheduled', 'Recent', 'All'];

const RECENT_STATUSES = new Set(['completed', 'closed', 'archived']);

function displayAddress(a: AppraisalSummary): string {
  if (a.property_address) return a.property_address;
  const parts = [a.address_street, a.address_city, a.address_state].filter(Boolean);
  return parts.length ? parts.join(', ') : '(no address)';
}

function matchesSearch(a: AppraisalSummary, q: string): boolean {
  if (!q) return true;
  const needle = q.toLowerCase();
  const hay = [
    a.policyholder_name,
    a.claim_number,
    a.property_address,
    a.address_street,
    a.address_city,
    a.address_state,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return hay.includes(needle);
}

function passesTab(a: AppraisalSummary, tab: Tab): boolean {
  if (tab === 'All') return true;
  const s = (a.status ?? '').toLowerCase();
  if (tab === 'Recent') return RECENT_STATUSES.has(s);
  // Scheduled: anything not completed/cancelled
  if (RECENT_STATUSES.has(s)) return false;
  if (s === 'cancelled') return false;
  // Treat empty/null status as scheduled (active work)
  return true;
}

function ageBucket(iso: string): 'today' | 'thisWeek' | 'older' {
  const d = new Date(iso);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (d >= startOfToday) return 'today';
  const sevenDaysAgo = new Date(startOfToday.getTime() - 7 * 24 * 60 * 60 * 1000);
  if (d >= sevenDaysAgo) return 'thisWeek';
  return 'older';
}

export function Home() {
  const [list, setList] = useState<AppraisalSummary[] | null>(null);
  const [syncMap, setSyncMap] = useState<Record<string, JobSyncSummary>>({});
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('Scheduled');
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await listAppraisals();
        if (!cancelled) setList(rows);
      } catch (e) {
        if (!cancelled) setErr((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Refresh sync statuses when the list loads and whenever connectivity flips on
  // (the queue may have drained while we were away).
  useEffect(() => {
    if (!list) return;
    let cancelled = false;
    const refresh = async () => {
      const entries = await Promise.all(
        list.map(async (a) => [a.id, await summarizeJobSync(a.id)] as const),
      );
      if (!cancelled) setSyncMap(Object.fromEntries(entries));
    };
    void refresh();
    const unsub = connectivity.onStatusChange(() => void refresh());
    const onFailed = () => void refresh();
    window.addEventListener('eva-field:sync-failed', onFailed);
    return () => {
      cancelled = true;
      unsub();
      window.removeEventListener('eva-field:sync-failed', onFailed);
    };
  }, [list]);

  const filtered = useMemo(() => {
    if (!list) return null;
    return list
      .filter((a) => passesTab(a, tab))
      .filter((a) => matchesSearch(a, query));
  }, [list, tab, query]);

  const grouped = useMemo(() => {
    if (!filtered) return null;
    const today: AppraisalSummary[] = [];
    const thisWeek: AppraisalSummary[] = [];
    const older: AppraisalSummary[] = [];
    for (const a of filtered) {
      const b = ageBucket(a.created_at);
      if (b === 'today') today.push(a);
      else if (b === 'thisWeek') thisWeek.push(a);
      else older.push(a);
    }
    return { today, thisWeek, older };
  }, [filtered]);

  return (
    <div className="mx-auto max-w-2xl px-4 pb-6">
      {/* Tabs */}
      <div className="sticky top-[49px] z-20 -mx-4 flex gap-1 border-b border-gray-200 bg-white px-4 pt-3">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`rounded-t-md px-3 py-2 text-sm font-semibold ${
              tab === t
                ? 'border-b-2 border-eva-teal text-eva-teal'
                : 'text-gray-500'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="mt-3">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search name, claim, address…"
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
        />
      </div>

      {err && (
        <div className="mt-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          Could not load appraisals: {err}
        </div>
      )}
      {!list && !err && <div className="mt-4 text-sm text-gray-500">Loading…</div>}

      {grouped && (
        <>
          <Section title="Today" items={grouped.today} syncMap={syncMap} />
          <Section title="This week" items={grouped.thisWeek} syncMap={syncMap} />
          <Section title="Older" items={grouped.older} syncMap={syncMap} />
          {grouped.today.length === 0 &&
            grouped.thisWeek.length === 0 &&
            grouped.older.length === 0 && (
              <div className="mt-6 rounded border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
                No jobs in this view.
              </div>
            )}
        </>
      )}
    </div>
  );
}

function Section({
  title,
  items,
  syncMap,
}: {
  title: string;
  items: AppraisalSummary[];
  syncMap: Record<string, JobSyncSummary>;
}) {
  if (items.length === 0) return null;
  return (
    <div className="mt-5">
      <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-gray-500">
        {title} <span className="opacity-50">({items.length})</span>
      </div>
      <ul className="space-y-2">
        {items.map((a) => (
          <JobCard key={a.id} appraisal={a} sync={syncMap[a.id]} />
        ))}
      </ul>
    </div>
  );
}

function JobCard({
  appraisal: a,
  sync,
}: {
  appraisal: AppraisalSummary;
  sync: JobSyncSummary | undefined;
}) {
  const syncStatus = sync?.status ?? 'idle';
  const border = statusBorderClass(syncStatus);
  return (
    <li>
      <Link
        to={`/job/${a.id}`}
        className={`block rounded-lg border border-gray-200 ${border} border-l-4 bg-white p-3 hover:bg-gray-50`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="truncate font-semibold">
              {a.policyholder_name || '(no name)'}
            </div>
            <div className="truncate text-xs text-gray-500">
              {a.claim_number ? `${a.claim_number} · ` : ''}
              {displayAddress(a)}
            </div>
            {syncStatus === 'pending-sync' && sync && sync.queuedCount > 0 && (
              <div className="mt-1 text-xs text-bracket-amber">
                {sync.queuedCount} queued
              </div>
            )}
            {syncStatus === 'in-progress' && (
              <div className="mt-1 text-xs text-bracket-blue">uploading…</div>
            )}
            {syncStatus === 'sync-failed' && sync && (
              <div className="mt-1 flex items-center gap-2 text-xs text-red-600">
                <span>Failed at #{sync.failedAtSequence ?? '?'}</span>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    void retryFailedItems(a.id).then(() => drain());
                  }}
                  className="rounded bg-red-50 px-2 py-0.5 font-semibold text-red-700"
                >
                  Retry
                </button>
              </div>
            )}
          </div>
          {a.status && (
            <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-gray-600">
              {a.status}
            </span>
          )}
          <span className="shrink-0 text-eva-teal">›</span>
        </div>
      </Link>
    </li>
  );
}
